import React from 'react';
import { LuBot, LuChartNoAxesCombined, LuPlus, LuSquareCheck, LuStickyNote } from 'react-icons/lu';
import { useNavigate } from 'react-router-dom';

import { FeatureFlag } from '@ndvr/auth-api';

import { CalendarIcon } from '@/components/Icons';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/utils/cn';
import { useFeatureFlag } from 'views/auth/useFeatureFlag';
import { useHasConnectedCalendar, useIsZoomConnected } from 'views/calendar/calendar-queries';
import { NewEventSheet } from 'views/calendar/widgets/NewEventSheet';
import { useDockState } from 'views/dock/dock-state';

import { AdHocMeetingDialog } from './AdHocMeetingDialog';

interface QuickActionsProps {
    isCollapsed?: boolean;
    label?: string;
    variant?: 'sidebar' | 'header';
}

export const QuickActions: React.FC<QuickActionsProps> = ({
    isCollapsed = false,
    label = 'Quick Actions',
    variant = 'sidebar',
}) => {
    const { addCreateTaskDockItem, addCreateNoteDockItem } = useDockState();
    const adHocMeetingsEnabled = useFeatureFlag(FeatureFlag.CC_AD_HOC_MEETINGS);
    const { data: hasCalendarConnected = false } = useHasConnectedCalendar();
    const { data: hasZoomConnected = false } = useIsZoomConnected();
    const canInviteClaire = adHocMeetingsEnabled && hasCalendarConnected && hasZoomConnected;
    const [isNewEventSheetOpen, setIsNewEventSheetOpen] = React.useState(false);
    const [isAdHocMeetingDialogOpen, setIsAdHocMeetingDialogOpen] = React.useState(false);
    const navigate = useNavigate();
    const isHeader = variant === 'header';
    const showButtonLabel = !isCollapsed && !isHeader;

    const DropdownButton = (
        <Button
            variant="ghost"
            size={isHeader || isCollapsed ? 'icon' : 'sm'}
            aria-label={label}
            className={cn(
                isHeader
                    ? 'text-cocoa-1000 bg-seafoam-200 hover:bg-seafoam-200/80 focus-visible:ring-seafoam-600 data-[state=open]:bg-seafoam-200/80 h-10 w-10 shrink-0 !gap-0 rounded-full p-0'
                    : 'hover:!bg-sidebar-hover focus:!bg-sidebar-hover data-[state=open]:!bg-sidebar-hover mx-2.5 mt-0.5 h-10 shrink-0 text-white',
                showButtonLabel ? 'px-2.5' : '',
                showButtonLabel ? 'flex items-center justify-start gap-2.5' : '',
            )}
        >
            <LuPlus className={isHeader ? 'h-5 w-5' : 'h-[18px] w-[18px]'} />
            {showButtonLabel && (
                <span className={cn('text-sm', isHeader ? 'text-espresso-600' : 'text-white')}>{label}</span>
            )}
        </Button>
    );

    const CreateTaskMenuItem: React.FC = () => (
        <DropdownMenuItem
            onSelect={() => {
                addCreateTaskDockItem();
            }}
            className="flex cursor-pointer items-center gap-2"
        >
            <LuSquareCheck className="h-4 w-4" />
            <span>Create a Task</span>
        </DropdownMenuItem>
    );

    const CreateNoteMenuItem: React.FC = () => (
        <DropdownMenuItem
            onSelect={() => {
                addCreateNoteDockItem();
            }}
            className="flex cursor-pointer items-center gap-2"
        >
            <LuStickyNote className="h-4 w-4" />
            <span>Create a Note</span>
        </DropdownMenuItem>
    );
    const ScheduleEventMenuItem: React.FC = () => (
        <DropdownMenuItem
            onSelect={() => {
                setIsNewEventSheetOpen(true);
            }}
            className="flex cursor-pointer items-center gap-2"
        >
            <CalendarIcon className="h-4 w-4" />
            <span>Schedule an Event</span>
        </DropdownMenuItem>
    );
    const InviteClaireMenuItem: React.FC = () => (
        <DropdownMenuItem
            onSelect={() => setIsAdHocMeetingDialogOpen(true)}
            className="flex cursor-pointer items-center gap-2"
        >
            <LuBot className="h-4 w-4" />
            <span>Invite Claire to a Meeting</span>
        </DropdownMenuItem>
    );
    const EnterInvestmentRequestMenuItem: React.FC = () => (
        <DropdownMenuItem
            onSelect={() => {
                navigate('/trading/investment-requests/create');
            }}
            className="flex cursor-pointer items-center gap-2"
        >
            <LuChartNoAxesCombined className="h-4 w-4" />
            <span>Enter an Investment Request</span>
        </DropdownMenuItem>
    );

    if (isCollapsed) {
        return (
            <>
                <Tooltip delayDuration={100}>
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <TooltipTrigger asChild>{DropdownButton}</TooltipTrigger>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" side="right" className="w-60">
                            <CreateTaskMenuItem />

                            <CreateNoteMenuItem />
                            <ScheduleEventMenuItem />
                            {canInviteClaire && <InviteClaireMenuItem />}
                            <EnterInvestmentRequestMenuItem />
                        </DropdownMenuContent>
                        <TooltipContent side="right">
                            <p>Quick Actions</p>
                        </TooltipContent>
                    </DropdownMenu>
                </Tooltip>
                <NewEventSheet open={isNewEventSheetOpen} onOpenChange={setIsNewEventSheetOpen} />
                {canInviteClaire && (
                    <AdHocMeetingDialog open={isAdHocMeetingDialogOpen} onOpenChange={setIsAdHocMeetingDialogOpen} />
                )}
            </>
        );
    }

    return (
        <>
            <DropdownMenu>
                <DropdownMenuTrigger asChild>{DropdownButton}</DropdownMenuTrigger>
                <DropdownMenuContent align={isHeader ? 'end' : 'center'} side="bottom" className="w-60">
                    <CreateTaskMenuItem />

                    <CreateNoteMenuItem />
                    <ScheduleEventMenuItem />
                    {canInviteClaire && <InviteClaireMenuItem />}
                    <EnterInvestmentRequestMenuItem />
                </DropdownMenuContent>
            </DropdownMenu>
            <NewEventSheet open={isNewEventSheetOpen} onOpenChange={setIsNewEventSheetOpen} />
            {canInviteClaire && (
                <AdHocMeetingDialog open={isAdHocMeetingDialogOpen} onOpenChange={setIsAdHocMeetingDialogOpen} />
            )}
        </>
    );
};
